import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { TEAM_ROLES, TEAM_TYPES, TEAM_TYPE_LABELS } from '../../../data/nurseAppModels';

const TeamListPage = () => {
  const { teams, currentTeam, setTeam, loading, cancelRequest } = useTeam();
  const { currentUser } = useAuth();
  const [teamRequests, setTeamRequests] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    fetchTeamRequests();
  }, [currentUser]);

  const fetchTeamRequests = async () => {
    if (!currentUser) return;

    try {
      const requestsRef = collection(db, 'teamRequests');
      const q = query(
        requestsRef,
        where('userId', '==', currentUser.uid),
        where('status', '==', 'PENDING'),
        orderBy('requestedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      setTeamRequests(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (error) {
      console.error('Error fetching team requests:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">ทีมของฉัน</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            + สร้างทีมใหม่
          </button>
        </div>

        {/* Team Requests */}
        {teamRequests.length > 0 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h2 className="font-semibold text-gray-800 mb-2">คำขอที่รอการอนุมัติ</h2>
            {teamRequests.map(request => (
              <div key={request.id} className="flex items-center justify-between p-3 bg-white rounded mb-2">
                <div className="flex-1">
                  <p className="font-medium">{request.teamName}</p>
                  <p className="text-sm text-gray-600">
                    {request.type === 'INVITATION' ? 'คำเชิญ' : 'คำขอเข้าร่วม'} • สถานะ: รอการอนุมัติ
                  </p>
                  {request.message && (
                    <p className="text-sm text-gray-500 mt-1">"{request.message}"</p>
                  )}
                </div>
                <button
                  onClick={async () => {
                    if (window.confirm('คุณแน่ใจหรือไม่ที่จะยกเลิกคำขอนี้?')) {
                      setCancelling(request.id);
                      const result = await cancelRequest(request.id);
                      if (result.success) {
                        fetchTeamRequests();
                      } else {
                        alert('เกิดข้อผิดพลาด: ' + result.error);
                      }
                      setCancelling(null);
                    }
                  }}
                  disabled={cancelling === request.id}
                  className="ml-4 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition disabled:opacity-50"
                >
                  {cancelling === request.id ? 'กำลังยกเลิก...' : 'ยกเลิก'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Teams List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : teams.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ยังไม่มีทีม</h3>
            <p className="text-gray-600 mb-4">สร้างทีมใหม่หรือเข้าร่วมทีมที่มีอยู่</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              สร้างทีมใหม่
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map(team => (
              <div
                key={team.id}
                onClick={() => setTeam(team)}
                className={`bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition ${
                  currentTeam?.id === team.id ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800">{team.name}</h3>
                  {currentTeam?.id === team.id && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">ทีมปัจจุบัน</span>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-4">{team.description || 'ไม่มีคำอธิบาย'}</p>
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <p className="text-gray-600">สมาชิก: {team.members?.length || 0} คน</p>
                    <p className="text-gray-600">บทบาทของคุณ: {team.userRole === TEAM_ROLES.LEADER ? 'หัวหน้าทีม' : 
                                                      team.userRole === TEAM_ROLES.DEPUTY_LEADER ? 'รองหัวหน้า' : 'สมาชิก'}</p>
                  </div>
                  <Link
                    to={`/nurse/teams/${team.id}`}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ดูรายละเอียด →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Team Modal */}
        {showCreateModal && (
          <CreateTeamModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchTeamRequests();
            }}
          />
        )}

        {/* Join Team Modal */}
        {showJoinModal && (
          <JoinTeamModal
            onClose={() => setShowJoinModal(false)}
            onSuccess={() => {
              setShowJoinModal(false);
              fetchTeamRequests();
            }}
          />
        )}
      </div>
    </div>
  );
};

// Create Team Modal Component
const CreateTeamModal = ({ onClose, onSuccess }) => {
  const { createTeam } = useTeam();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'DEPARTMENT',
    maxMembers: 50
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await createTeam(formData);
    if (result.success) {
      onSuccess();
    } else {
      alert('เกิดข้อผิดพลาด: ' + result.error);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">สร้างทีมใหม่</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ชื่อทีม *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="เช่น แผนกอายุรกรรม"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              คำอธิบาย
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="อธิบายเกี่ยวกับทีม..."
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ประเภททีม
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(TEAM_TYPES).map(([key, value]) => (
                <option key={value} value={value}>
                  {TEAM_TYPE_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'กำลังสร้าง...' : 'สร้างทีม'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Join Team Modal Component
const JoinTeamModal = ({ onClose, onSuccess }) => {
  const { joinTeam } = useTeam();
  const [teamId, setTeamId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await joinTeam(teamId, message);
    if (result.success) {
      alert('ส่งคำขอเข้าร่วมทีมสำเร็จ');
      onSuccess();
    } else {
      alert('เกิดข้อผิดพลาด: ' + result.error);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">เข้าร่วมทีม</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team ID *
            </label>
            <input
              type="text"
              required
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="กรอก Team ID"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ข้อความ (ไม่บังคับ)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="ข้อความถึงหัวหน้าทีม..."
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'กำลังส่ง...' : 'ส่งคำขอ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamListPage;


